import { ErrorDetail, SigningRequest, SigningRequestClient } from '@microsoft/esign.gateway.service.client';
import { Constants } from '../../../common/Constants';
import { ApprovalRequestType } from '../../../data/types.generated';
import {
    getTelemetryErrorTypeFromStatus,
    PanelRegion,
    PanelType,
    ScenarioName,
    StatusName,
    StepName,
    SubWorkLoad,
} from '../../../logger';
import { getEsignGatewayProvider } from '../../../utilities/EsignUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { delay } from '../../../utilities/Utils';
import { fetchAndLog, getEsignGatewayConfiguration } from '../utils/Authentication/EsignAuthHelpers';

export const createEsignApproval = async (
    approvalRequestType: ApprovalRequestType,
    signingRequestFn: () => SigningRequest
): Promise<string> => {
    const logger = getAppHost().getLogger();

    // CreateApprovals is started by the parent page; this child scenario must exist first or markScenario no-ops.
    logger.extendScenario(ScenarioName.CreateEsignApproval, ScenarioName.CreateApprovals);

    try {
        const signingRequest = signingRequestFn();
        const esignGatewayConfiguration = await getEsignGatewayConfiguration(approvalRequestType);
        const provider = getEsignGatewayProvider(approvalRequestType);
        const signingRequestClient = new SigningRequestClient(esignGatewayConfiguration, null, {
            fetch: fetchAndLog,
        });
        const signingRequestId = await signingRequestClient.createSigningRequest(provider, signingRequest);

        // The submit-button event only tells us the user tried. This is the first point where the provider
        // has actually accepted the document, so it is what the "signing requests created" funnel step counts.
        logger.markScenario(ScenarioName.CreateEsignApproval, StepName.EsignSigningRequestCreated, StatusName.NA);
        logger.logEsignUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateEsignApproval,
            StepName.EsignSigningRequestCreated,
            'none',
            'success',
            'createEsignApproval',
            'esignSigningRequestCreated',
            PanelRegion.Modal,
            PanelType.CreateEsignApproval,
            {
                ApprovalType: approvalRequestType,
            }
        );

        const signingRequestUrl = (await signingRequestClient.getSigningRequest(signingRequestId, provider)).url;
        await delay(Constants.screenReaderWaitTime);
        logger.stopScenario(ScenarioName.CreateEsignApproval);
        return signingRequestUrl;
    } catch (error: any) {
        logger.logTraceHttpException('CreateEsignApproval', error.message, error.status);

        let errorDetail: ErrorDetail | null;
        try {
            errorDetail = JSON.parse(error.response);
        } catch {
            errorDetail = null;
        }

        const errorType = getTelemetryErrorTypeFromStatus(error.status);
        const errorCode = errorDetail?.innerError?.code;
        const failureEventData = {
            message: error.message,
            data: {
                errorType,
                errorCode: errorCode ?? errorType,
                httpCode: error.status ?? 0,
            },
        };

        logger.markScenario(
            ScenarioName.CreateEsignApproval,
            StepName.EsignSigningRequestFailed,
            StatusName.Fail,
            failureEventData
        );
        logger.failScenario(ScenarioName.CreateEsignApproval, failureEventData);
        logger.logEsignUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateEsignApproval,
            StepName.EsignSigningRequestFailed,
            'none',
            'failure',
            'createEsignApproval',
            'esignSigningRequestCreated',
            PanelRegion.Modal,
            PanelType.CreateEsignApproval,
            {
                ApprovalType: approvalRequestType,
                errorType,
                errorCode,
                responseCode: error.status,
            }
        );

        throw new Error(errorCode?.toLowerCase());
    }
};
